'use client';

import Linkify from 'linkify-react';

import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { Textarea } from '@/components/ui/textarea';

import { debounce } from '@/utils/common';
import { cn } from '@/utils/styles';

type EditableTextProps = {
	/** The text value to display or edit. */
	value: string;
	/** Called with the new value on each keystroke (debounced 250 ms). */
	onChange?: (value: string) => void;
	/** Called with the final value when editing ends (blur). Fires undebounced, so a
	 *  consumer can persist the committed text. */
	onBlur?: (value: string) => void;
	onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
	onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	placeholder?: string;
	/** Max height of the textarea in pixels. Defaults to 200. */
	maxHeight?: number;
	/** Classes applied to the display div element. */
	className?: string;
	/** Classes applied to the textarea element. */
	textareaClassName?: string;
};

/**
 * Toggles between a read-only linkified div and a self-expanding textarea.
 * Editing state is managed internally — the textarea appears on focus/click and
 * reverts to the display view on blur. Starts in edit mode when value is empty
 * so newly-added fields are immediately typeable.
 */
const EditableText = forwardRef<HTMLTextAreaElement, EditableTextProps>(
	(
		{
			value,
			onChange,
			onBlur,
			onFocus,
			onKeyDown,
			placeholder,
			maxHeight = 200,
			className,
			textareaClassName,
		},
		forwardedRef
	) => {
		// Internal text state prevents cursor-jump: the textarea is controlled
		// by this state, not the parent prop, so re-renders don't reset caret.
		const [text, setText] = useState(value);
		const [isEditing, setIsEditing] = useState(!value);
		const textareaRef = useRef<HTMLTextAreaElement | null>(null);

		// eslint-disable-next-line react-hooks/exhaustive-deps
		const notifyChange = useCallback(
			debounce((v: unknown) => onChange?.(v as string), 250),
			[onChange]
		);

		// useLayoutEffect runs synchronously after DOM mutations and before paint,
		// so height is correct in the same frame — no resize flicker on mode switch.
		useLayoutEffect(() => {
			const el = textareaRef.current;
			if (!el) return;
			el.style.height = 'auto';
			el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
		}, [text, isEditing, maxHeight]);

		const assignRef = useCallback(
			(el: HTMLTextAreaElement | null) => {
				textareaRef.current = el;
				if (typeof forwardedRef === 'function') forwardedRef(el);
				else if (forwardedRef) forwardedRef.current = el;
			},
			[forwardedRef]
		);

		const handleChange = useCallback(
			(e: React.ChangeEvent<HTMLTextAreaElement>) => {
				setText(e.target.value);
				notifyChange(e.target.value);
			},
			[notifyChange]
		);

		const handleTextareaFocus = useCallback(
			(e: React.FocusEvent<HTMLTextAreaElement>) => {
				setIsEditing(true);
				onFocus?.(e);
			},
			[onFocus]
		);

		const handleTextareaBlur = useCallback(() => {
			setIsEditing(false);
			// Flush immediately (bypassing the debounce) so a consumer reading the value
			// right after blur — e.g. a Send button clicked straight from the field — sees
			// the final text; blur fires before the button's click.
			onChange?.(text);
			onBlur?.(text);
		}, [onChange, onBlur, text]);

		const handleDisplayClick = useCallback(() => {
			setIsEditing(true);
			setTimeout(() => {
				const el = textareaRef.current;
				if (!el) return;
				el.focus();
				// Drop the caret at the end rather than the start, so editing continues
				// from where the text leaves off.
				const end = el.value.length;
				el.setSelectionRange(end, end);
			}, 0);
		}, []);

		if (isEditing) {
			return (
				<Textarea
					value={text}
					rows={1}
					placeholder={placeholder}
					onChange={handleChange}
					onFocus={handleTextareaFocus}
					onBlur={handleTextareaBlur}
					onKeyDown={onKeyDown}
					ref={assignRef}
					style={{ maxHeight: `${maxHeight}px` }}
					className={cn(
						'h-fit min-h-fit w-full resize-none overflow-y-auto overflow-x-hidden rounded-none border-none p-0 text-primary outline-none placeholder:text-slate-400 focus-visible:ring-transparent focus-visible:ring-offset-0',
						textareaClassName
					)}
				/>
			);
		}

		if (!text) return null;

		return (
			<div
				role="button"
				tabIndex={0}
				className={cn(
					'cursor-text whitespace-pre-line text-primary [&_a]:text-primary-blue',
					className
				)}
				onClick={handleDisplayClick}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') handleDisplayClick();
				}}
			>
				<Linkify options={{ target: '_blank' }}>{text}</Linkify>
			</div>
		);
	}
);

EditableText.displayName = 'EditableText';

export default EditableText;
